import type { ItemHelp, HelpAction } from "../fuxi/yufa-practice.js";
import type { ItemAnswer } from "../fuxi/yufa-marking.js";
import type { HandoverCount } from "./lianxi-handover.js";
import type { KeptQueue } from "./lianxi-yufa-queue.js";

/**
 * What keeps a reader's place in a grammar exercise 语法练习 outside the
 * page.
 *
 * `lianxi-yufa.ts` tells a keeper each answer and adopts what it answers,
 * and `lianxi-yufa-recorder.ts` is the one keeper, posting to the
 * `/user/lianxi/` handler. The public page has none, and keeps nothing.
 * `lianxi-keeper.ts` is the same for a writing drill.
 */
export interface YufaKeeper {
  /**
   * Whether the finish has handed the exercise's sentences to 复习, as far
   * as the keeper has heard. The finish line says so.
   */
  readonly handedOver: boolean;

  /** Whether the reader holds no Pro 专业, which the finish line names. */
  readonly free?: boolean | undefined;

  /** How many cards the hand-over put in and when the first is due, once heard. */
  readonly handoverCount?: HandoverCount | undefined;

  /**
   * An item answered, right or wrong, for the handler to mark and keep.
   * @param item - The item, by its index in the exercise's order.
   * @param answer - What the reader produced.
   * @returns The queue as kept, or nothing where the keeper could not say.
   */
  answered(
    item: number,
    answer: ItemAnswer,
    help?: ItemHelp,
  ): Promise<KeptQueue | undefined>;

  helped?(item: number, help: HelpAction): Promise<KeptQueue | undefined>;

  /** The exercise started again from the first item. */
  again(): Promise<KeptQueue | undefined | void>;
}

/**
 * What the exercise does once the keeper has answered.
 *
 * The queue the keeper answered is adopted over the page's own arithmetic.
 * Nothing is done where nothing was asked, or where nothing came back, since
 * the page's own arithmetic already stands. `settleAfterPass` in
 * `./lianxi-keeper.ts` is the same for a writing drill.
 * @param kept - The keeper's answer, where anything was asked.
 * @param adopt - Adopt the queue the keeper answered.
 */
export function settleAfterAnswer(
  kept: Promise<KeptQueue | undefined> | undefined,
  adopt: (kept: KeptQueue) => void,
): void {
  void kept?.then((answer) => {
    if (answer !== undefined) {
      adopt(answer);
    }
  });
}
